import { Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { userGroupApi } from '~/apis/userGroupApi'
import { useUserState } from '~/atoms/UserState'
import { toaster } from '~/components/ui/toaster'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { getUserRoleClassification } from '~/utils/auth'

import { LoadingState } from '@mangovoice/mango-ui'
import {
  useGetUserGroup,
  UserGroupError,
  UserGroupForm,
  useUpdateUserGroup,
} from '@mangovoice/user-groups-ui'
import type {
  UserGroupCreate,
  UserGroupUpdate,
} from '@mangovoice/user-groups-ui'

export const UserGroupEditPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { userGroupId } = useParams<{ userGroupId: string }>()
  const { pbxId, userRoles } = useUserState({ canAutoRefresh: false })

  const parsedPbxId = pbxId ?? NaN
  const parsedGroupId = userGroupId ? parseInt(userGroupId, 10) : NaN
  const { isMangoStaff, isSuperuser } = getUserRoleClassification(userRoles)
  const canEditAccessFields = isMangoStaff || isSuperuser

  const { userGroup, isLoading, error } = useGetUserGroup({
    api: userGroupApi,
    pbxId: parsedPbxId,
    id: parsedGroupId,
    queryOptions: {
      enabled: !Number.isNaN(parsedPbxId) && !Number.isNaN(parsedGroupId),
    },
  })

  const updateMutation = useUpdateUserGroup({
    api: userGroupApi,
    pbxId: parsedPbxId,
  })

  const handleSubmit = (data: UserGroupUpdate) => {
    updateMutation.mutate(
      { id: parsedGroupId, data },
      {
        onSuccess: () => {
          toaster.create({
            title: t('userGroups.toast.updatedTitle', 'User group updated'),
            type: 'success',
            duration: 4000,
          })
          navigate(ApplicationRoutes.USER_GROUPS)
        },
        onError: (err: Error) => {
          if (err instanceof UserGroupError && err.code === 'forbidden') {
            toaster.create({
              title: t(
                'userGroups.toast.authRequiredTitle',
                'Authentication required',
              ),
              description: t(
                'userGroups.toast.authRequiredDescription',
                'Your session may have expired. Please re-authenticate and try again.',
              ),
              type: 'error',
              duration: 6000,
            })
          } else {
            toaster.create({
              title: t(
                'userGroups.toast.updateFailedTitle',
                'Failed to update user group',
              ),
              description: err.message,
              type: 'error',
              duration: 5000,
            })
          }
        },
      },
    )
  }

  if (!pbxId) {
    return null
  }

  if (isLoading) {
    return (
      <LoadingState
        layout="alert"
        title={t('userGroups.form.loading', 'Loading user group...')}
      />
    )
  }

  if (error) {
    return (
      <Text color="fg.error">
        {t(
          'userGroups.form.loadingError',
          'Error loading user group: {{message}}',
          {
            message: (error as Error).message,
          },
        )}
      </Text>
    )
  }

  if (!userGroup) {
    return null
  }

  return (
    <UserGroupForm
      api={userGroupApi}
      pbxId={parsedPbxId}
      mode="edit"
      userGroup={userGroup}
      isSubmitting={updateMutation.isPending}
      canEditAccessFields={canEditAccessFields}
      onSubmit={(data: UserGroupCreate | UserGroupUpdate) =>
        handleSubmit(data as UserGroupUpdate)
      }
      onCancel={() => navigate(ApplicationRoutes.USER_GROUPS)}
      t={t}
      locale={i18n.language}
    />
  )
}
